import { useCoverageBetaNextTaskPrefetch } from "@/hooks/useCoverageBetaNextTaskPrefetch";
import { useCoverageBetaStore } from "@/store/useCoverageBetaStore";
import {
  buildCoverageBetaQaResult,
  isQaReadyToSubmit,
} from "@/utils/build-coverage-beta-qa-result";
import { CompleteCoverageBetaQaPayloadSchema } from "@/utils/zod-schemas/complete-coverage-beta-qa-payload-schema";
import { isTaskAlreadyFinalized } from "@/utils/task-already-finalized";
import { mvpApiCompleteJobTask } from "@labeling-tools/api/lebron/generated";
import { mvpApiGetNextPendingJobTaskOptions } from "@labeling-tools/api/lebron/generated/@tanstack/react-query.gen";
import { JobTaskDetailSchema } from "@labeling-tools/api/lebron/generated/types.gen";
import { Button } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";

interface Props {
  taskDetailResp: JobTaskDetailSchema | undefined;
}

/**
 * Submits a Coverage Beta QA task's verdicts. This button only ever renders
 * for a labeler actually taking the QA task — CoverageBetaHeader returns null
 * on admin routes before reaching here, so (unlike CoverageBetaSubmitBtn)
 * there's no admin-browse render to guard prefetch against.
 */
export const CoverageBetaQaSubmitBtn = (props: Props) => {
  const { taskDetailResp } = props;
  const params = useParams<{ jobId: string }>();
  const job_id = params?.jobId ?? "";
  const task_id = taskDetailResp?.id ?? "";
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();

  const { nextTaskId, nextTaskDetail, isPrefetchReady } =
    useCoverageBetaNextTaskPrefetch({
      jobId: job_id,
      currentTaskId: task_id,
      enabled: !!job_id && !!task_id,
    });

  // Select the raw slices (cheap) and only run the list rebuild + `.every()`
  // walk in the render body — doing it inside the selector itself would
  // re-run on every store update (any hover/brightness/etc. change), not
  // just ones that actually touch these fields.
  const isQaMode = useCoverageBetaStore((s) => s.isQaMode);
  const idxToFeEmptyArea = useCoverageBetaStore((s) => s.idxToFeEmptyArea);
  const persons = useCoverageBetaStore((s) => s.persons);
  const qaVerdicts = useCoverageBetaStore((s) => s.qaVerdicts);
  const enableSubmit = useMemo(
    () =>
      !!task_id &&
      isQaReadyToSubmit({ isQaMode, idxToFeEmptyArea, persons, qaVerdicts }),
    [task_id, isQaMode, idxToFeEmptyArea, persons, qaVerdicts],
  );

  let disabledReason = "";
  if (!task_id || !isQaMode) {
    disabledReason = "Loading the submission under review…";
  } else if (!enableSubmit) {
    disabledReason = "Review every box before submitting";
  }

  const handleSubmit = async () => {
    const state = useCoverageBetaStore.getState();
    // Re-check against live store state rather than trusting the render-time
    // `enableSubmit`: between tasks the store resets a frame before this
    // button re-renders, so a fast second click can land while the button
    // still looks enabled — and would submit an empty verdict set for the
    // next task. See `isQaReadyToSubmit`.
    if (!task_id || !isQaReadyToSubmit(state)) return;

    setIsSubmitting(true);
    try {
      const result = buildCoverageBetaQaResult(state);

      const parsedResult = CompleteCoverageBetaQaPayloadSchema.safeParse({
        result,
      });

      if (!parsedResult.success) {
        throw new Error("CompleteCoverageBetaQaPayloadSchema parsing error");
      }

      const body = parsedResult.data;

      const { error } = await mvpApiCompleteJobTask({
        body,
        path: { job_id, task_id },
      });
      if (error && !isTaskAlreadyFinalized(error)) throw error;

      const nextPendingKey = mvpApiGetNextPendingJobTaskOptions({
        path: { job_id },
      }).queryKey;

      queryClient.invalidateQueries({ queryKey: ["job-tasks", job_id] });

      if (isPrefetchReady && nextTaskId && nextTaskDetail) {
        queryClient.setQueryData(nextPendingKey, nextTaskDetail);
        queryClient.invalidateQueries({
          queryKey: nextPendingKey,
          exact: true,
        });
      } else {
        await queryClient.invalidateQueries({
          queryKey: nextPendingKey,
          exact: true,
        });
      }
    } catch (error) {
      console.error("error: ", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Button
      size="sm"
      onClick={handleSubmit}
      loading={isSubmitting}
      disabled={!enableSubmit}
      title={disabledReason || undefined}
    >
      Submit QA Review
    </Button>
  );
};
